import React, { useMemo } from 'react'
import styles from './MealStatsScreen.module.css'
import { Cell, Column, CompactTable, Row, Table, TableBody, TableHeader } from '@/components/Table'
import { UserNutritionalNeeds, WeekPlan } from '@/api/types'
import { groupBy } from '@/utils'
import { getNutritionDay, getWeekPlanIngredientsCount, IngredientRow } from '../utils'

type RecipeStats = {
  name: string
  count: number
}

type Stats = {
  ingredients: Array<IngredientRow>
  caloriesPerDayNeeded: number
  caloriesPerDay: number
  avgProteinsPerDay: number
  proteinsPerDayNeeded: number
  avgCarbsPerDay: number
  carbsPerDayNeeded: number
  avgFatsPerDay: number
  fatsPerDayNeeded: number
  avgFiberPerDay: number
  fiberPerDayNeeded: number
}

type MealStatsScreenProps = {
  weekPlan: WeekPlan
  userNutritionalNeeds?: UserNutritionalNeeds
  userId: number
  priorityIngredients: Array<number>
}

function getAvgNutrientsPerDay(weekPlan: WeekPlan, userId: number) {
  const groupedByDays = groupBy(weekPlan.mealPlans, 'dayOfWeek')
  const totalNutrients = {
    calories: 0,
    proteins: 0,
    carbs: 0,
    fats: 0,
    fiber: 0,
  }

  for (const day in groupedByDays) {
    const nutrition = getNutritionDay(groupedByDays[day], userId)
    totalNutrients.calories += nutrition.energy
    totalNutrients.proteins += nutrition.protein
    totalNutrients.carbs += nutrition.carbs
    totalNutrients.fats += nutrition.fat
    totalNutrients.fiber += nutrition.fiber
  }
  const daysCount = Object.keys(groupedByDays).length
  return {
    calories: totalNutrients.calories / daysCount,
    proteins: totalNutrients.proteins / daysCount,
    carbs: totalNutrients.carbs / daysCount,
    fats: totalNutrients.fats / daysCount,
    fiber: totalNutrients.fiber / daysCount,
  }
}

function getWeekPlanStats(
  weekPlan: WeekPlan,
  userId: number,
  userNutritionalNeeds?: UserNutritionalNeeds,
): Stats {
  const ingredients = getWeekPlanIngredientsCount(weekPlan)
  const recipes = []
  const nutrientsAvg = getAvgNutrientsPerDay(weekPlan, userId)

  const caloriesPerDayNeeded = userNutritionalNeeds?.nutrientNeeds.KCAL || 0
  const proteinsPerDayNeeded = userNutritionalNeeds?.nutrientNeeds.PROTEIN || 0
  const carbsPerDayNeeded = userNutritionalNeeds?.nutrientNeeds.CARB || 0
  const fatsPerDayNeeded = userNutritionalNeeds?.nutrientNeeds.FAT || 0
  const fiberPerDayNeeded = userNutritionalNeeds?.nutrientNeeds.FIBER || 0

  return {
    ingredients,
    caloriesPerDayNeeded,
    caloriesPerDay: nutrientsAvg.calories,
    avgProteinsPerDay: nutrientsAvg.proteins,
    proteinsPerDayNeeded,
    avgCarbsPerDay: nutrientsAvg.carbs,
    carbsPerDayNeeded,
    avgFatsPerDay: nutrientsAvg.fats,
    fatsPerDayNeeded,
    avgFiberPerDay: nutrientsAvg.fiber,
    fiberPerDayNeeded,
  }
}

export function MealStatsScreen({
  weekPlan,
  userNutritionalNeeds,
  priorityIngredients,
  userId,
}: MealStatsScreenProps) {
  const stats = useMemo(() => {
    return getWeekPlanStats(weekPlan, userId, userNutritionalNeeds)
  }, [weekPlan, userNutritionalNeeds, userId])

  const priorityIngredientsSet = useMemo(
    () => new Set(priorityIngredients),
    [priorityIngredients],
  )

  const macroRows = [
    {
      label: 'Calories/day',
      actual: Math.round(stats.caloriesPerDay),
      needed: stats.caloriesPerDayNeeded,
      unit: 'kcal',
    },
    {
      label: 'Proteins/day',
      actual: Math.round(stats.avgProteinsPerDay),
      needed: stats.proteinsPerDayNeeded,
      unit: 'g',
    },
    {
      label: 'Carbs/day',
      actual: Math.round(stats.avgCarbsPerDay),
      needed: stats.carbsPerDayNeeded,
      unit: 'g',
    },
    {
      label: 'Fats/day',
      actual: Math.round(stats.avgFatsPerDay),
      needed: stats.fatsPerDayNeeded,
      unit: 'g',
    },
    {
      label: 'Fiber/day',
      actual: Math.round(stats.avgFiberPerDay),
      needed: stats.fiberPerDayNeeded,
      unit: 'g',
    },
  ].map((m) => ({
    ...m,
    diff: +(m.actual - m.needed).toFixed(1),
  }))

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <h3>Calories & Macros</h3>
        <CompactTable
          style={{ marginTop: '10px' }}
          search={false}
          aria-label="Macros"
          columns={[
            { label: 'Metric', key: 'label' },
            { label: 'Actual', key: 'actual' },
            { label: 'Needed', key: 'needed' },
            { label: 'Diff', key: 'diff' },
            { label: 'Unit', key: 'unit' },
          ]}
          rows={macroRows}
        />
      </div>

      <div className={styles.section}>
        <h3>Used Ingredients</h3>
        <div className={styles.ingredientsTableWrapper}>
          <Table aria-label="Top Ingredients">
            <TableHeader>
              <Column isRowHeader>Name</Column>
              <Column>English name</Column>
              <Column className={styles.numericHeader}>Count</Column>
            </TableHeader>
            <TableBody items={stats.ingredients}>
              {(ingredient) => {
                const isPriority = priorityIngredientsSet.has(ingredient.id)

                return (
                  <Row
                    key={ingredient.id}
                    id={ingredient.id}
                    className={isPriority ? styles.priorityIngredientRow : undefined}>
                    <Cell>{ingredient.name}</Cell>
                    <Cell>{ingredient.englishName}</Cell>
                    <Cell className={styles.numericCell}>{ingredient.count}</Cell>
                  </Row>
                )
              }}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
